import React, { useState } from "react";
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { Plus, MoreVertical, Edit, Trash, UserPlus } from "lucide-react";
import ProductSection from "../../ProductsModule/Components/ProductSection";

const UserPermissions = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Sample users data
  const users = [
    {
      id: 1,
      name: "John Doe",
      email: "john.doe@example.com",
      role: "Admin",
      status: "Active",
    },
    {
      id: 2,
      name: "Jane Smith",
      email: "jane.smith@example.com",
      role: "Manager",
      status: "Active",
    },
    {
      id: 3,
      name: "Robert Johnson",
      email: "robert.johnson@example.com",
      role: "Sales",
      status: "Inactive",
    },
    {
      id: 4,
      name: "Emily Williams",
      email: "emily.williams@example.com",
      role: "Inventory",
      status: "Active",
    },
  ];

  // Sample roles data
  const roles = [
    {
      id: 1,
      name: "Admin",
      description: "Full access to all features",
      permissions: [
        "manage_users",
        "manage_products",
        "manage_orders",
        "manage_finances",
        "view_reports",
      ],
      userCount: 2,
    },
    {
      id: 2,
      name: "Manager",
      description: "Access to most features except user management",
      permissions: [
        "manage_products",
        "manage_orders",
        "manage_finances",
        "view_reports",
      ],
      userCount: 3,
    },
    {
      id: 3,
      name: "Sales",
      description: "Access to orders and customers",
      permissions: ["manage_orders", "view_reports"],
      userCount: 5,
    },
    {
      id: 4,
      name: "Inventory",
      description: "Access to products and inventory",
      permissions: ["manage_products", "view_reports"],
      userCount: 2,
    },
  ];

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  return (
    <div className="space-y-8">
      {/* Users Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <ProductSection data={"Users"} />
          <Button
            variant="contained"
            startIcon={<UserPlus size={16} />}
            sx={{
              backgroundColor: "#5652B0",
              color: "white",
              textTransform: "none",
              fontFamily: "Outfit",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            Add User
          </Button>
        </div>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", border: "1px solid #E5E7EB" }}>
          <Table sx={{ minWidth: 650 }} aria-label="users table">
            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Email
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Role
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(user => (
                <TableRow
                  key={user.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontFamily: "Outfit" }}>
                    {user.name}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit" }}>
                    {user.email}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit" }}>
                    <Chip
                      label={user.role}
                      size="small"
                      sx={{
                        backgroundColor: "#F1F0FF",
                        color: "#5652B0",
                        fontFamily: "Outfit",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit" }}>
                    <Chip
                      label={user.status}
                      size="small"
                      sx={{
                        backgroundColor:
                          user.status === "Active" ? "#ECFDF5" : "#FEF2F2",
                        color: user.status === "Active" ? "#10B981" : "#EF4444",
                        fontFamily: "Outfit",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="user actions"
                      onClick={e => handleMenuOpen(e, user)}
                      size="small">
                      <MoreVertical size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 2,
            sx: {
              minWidth: 150,
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
            },
          }}>
          <MenuItem
            onClick={handleMenuClose}
            sx={{ fontFamily: "Outfit", fontSize: "14px" }}>
            <Edit size={16} className="mr-2" /> Edit
          </MenuItem>
          <MenuItem
            onClick={handleMenuClose}
            sx={{ fontFamily: "Outfit", fontSize: "14px", color: "#EF4444" }}>
            <Trash size={16} className="mr-2" /> Delete
          </MenuItem>
        </Menu>
      </div>

      {/* Roles & Permissions */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <ProductSection data={"Roles & Permissions"} />
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            sx={{
              backgroundColor: "#5652B0",
              color: "white",
              textTransform: "none",
              fontFamily: "Outfit",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            Add Role
          </Button>
        </div>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", border: "1px solid #E5E7EB" }}>
          <Table sx={{ minWidth: 650 }} aria-label="roles table">
            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Role
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Users
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: "Outfit" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map(role => (
                <TableRow
                  key={role.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontFamily: "Outfit", fontWeight: 500 }}>
                    {role.name}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit" }}>
                    {role.description}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit" }}>
                    {role.userCount}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      startIcon={<Edit size={16} />}
                      size="small"
                      sx={{
                        borderColor: "#5652B0",
                        color: "#5652B0",
                        textTransform: "none",
                        fontFamily: "Outfit",
                        mr: 1,
                        "&:hover": {
                          borderColor: "#4A47A3",
                          backgroundColor: "rgba(86, 82, 176, 0.04)",
                        },
                      }}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default UserPermissions;
